/**
 * Sanitização segura de HTML para o Geppetto Builder.
 * Remove scripts, event handlers, javascript: URLs e outros vetores XSS.
 * Permite HTML de conteúdo seguro para uso no builder e export para Moodle.
 *
 * @see https://github.com/cure53/DOMPurify
 */
import DOMPurify from 'dompurify';
import { isSelfHostedH5pIframePath } from './embedContentUtils.js';
import { normalizeQuillListHtml } from './quillHtmlNormalize.js';

/** Tags permitidas para conteúdo HTML (sem iframe - uso restrito) */
const ALLOWED_TAGS_CONTENT = [
  'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
  'p', 'div', 'span', 'br', 'hr',
  'strong', 'em', 'b', 'i', 'u', 's', 'sub', 'sup',
  'ul', 'ol', 'li',
  'a', 'img',
  'blockquote', 'pre', 'code', 'kbd',
  'table', 'thead', 'tbody', 'tfoot', 'tr', 'th', 'td',
  'section', 'article', 'header', 'footer', 'nav', 'aside', 'main',
  'figure', 'figcaption',
  'mark', 'small', 'del', 'ins', 'abbr', 'cite', 'q',
  'dl', 'dt', 'dd',
  'col', 'colgroup', 'caption',
];

/** Atributos permitidos por tag */
const ALLOWED_ATTR = [
  'href', 'target', 'rel', 'title', 'class', 'id',
  'src', 'alt', 'width', 'height',
  'colspan', 'rowspan', 'align', 'valign',
  'style',
];

/** Tags adicionais quando permitir iframes (embeds de vídeo/H5P) */
const ALLOWED_TAGS_WITH_IFRAME = [...ALLOWED_TAGS_CONTENT, 'iframe'];

/** Tags de casca de documento — nunca devem ir no export Moodle embutido em index.html */
const DOCUMENT_SHELL_TAGS = new Set(['html', 'head', 'body', 'title', 'meta', 'link']);

/** Tags para documento HTML completo (html, head, body, etc.) */
const ALLOWED_TAGS_FULL_DOC = [
  'html', 'head', 'body', 'title', 'meta', 'link',
  'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
  'p', 'div', 'span', 'br', 'hr',
  'strong', 'em', 'b', 'i', 'u', 's', 'sub', 'sup',
  'ul', 'ol', 'li',
  'a', 'img',
  'blockquote', 'pre', 'code', 'kbd',
  'table', 'thead', 'tbody', 'tfoot', 'tr', 'th', 'td',
  'section', 'article', 'header', 'footer', 'nav', 'aside', 'main',
  'figure', 'figcaption',
  'mark', 'small', 'del', 'ins', 'abbr', 'cite', 'q',
  'dl', 'dt', 'dd',
  'col', 'colgroup', 'caption',
  'style', 'iframe',
];

/** Atributos adicionais para documento completo (body bgcolor, etc.) */
const ALLOWED_ATTR_FULL_DOC = [
  ...ALLOWED_ATTR,
  'bgcolor', 'text', 'link', 'vlink', 'alink',
  'charset', 'content', 'http-equiv', 'name',
  'media', 'type', 'rel',
];

/** Domínios permitidos para iframe src (vídeo, H5P, Genially) */
const ALLOWED_IFRAME_ORIGINS = [
  'youtube.com', 'youtube-nocookie.com', 'www.youtube.com',
  'vimeo.com', 'player.vimeo.com', 'www.vimeo.com',
  'h5p.org', 'h5p.com',
  'genially.com',
  'genial.ly',
  'embed.ted.com',
];

/**
 * Verifica se a URL do iframe é de um domínio permitido
 */
function isAllowedIframeSrc(src) {
  if (!src || typeof src !== 'string') return false;
  try {
    const url = new URL(src.trim());
    if (url.protocol !== 'https:' && url.protocol !== 'http:') return false;
    const host = url.hostname.toLowerCase();
    const originOk = ALLOWED_IFRAME_ORIGINS.some(origin => host === origin || host.endsWith('.' + origin));
    return originOk || isSelfHostedH5pIframePath(src);
  } catch {
    return false;
  }
}

/**
 * Configuração base para sanitização
 */
const baseConfig = {
  ALLOWED_TAGS: ALLOWED_TAGS_CONTENT,
  ALLOWED_ATTR,
  ALLOW_DATA_ATTR: false,
  ADD_ATTR: ['target'],
};

/**
 * Sanitiza HTML para conteúdo seguro (texto, alert, card, post-it, etc).
 * Remove scripts, event handlers, javascript:, data: (scripts), etc.
 *
 * @param {string} dirty - HTML a ser sanitizado
 * @returns {string} HTML limpo e seguro
 */
export function sanitizeHtml(dirty) {
  if (!dirty || typeof dirty !== 'string') return '';
  return DOMPurify.sanitize(normalizeQuillListHtml(dirty), baseConfig);
}

/**
 * Troca hífen ASCII entre duas letras por hífen não-separável (U+2011), só fora de tags HTML.
 * Evita quebra de linha em compostos como "prepare-se", "anti-gás" (Unicode line-break após HY).
 *
 * @param {string} html - HTML já sanitizado
 * @returns {string}
 */
export function applyNonBreakingHyphenBetweenLetters(html) {
  if (!html || typeof html !== 'string') return html;
  return html.split(/(<[^>]*>)/g).map((segment) => {
    if (segment.startsWith('<')) return segment;
    return segment.replace(/([\p{L}])-([\p{L}])/gu, '$1\u2011$2');
  }).join('');
}

/**
 * Sanitiza HTML permitindo iframes de fontes confiáveis (YouTube, Vimeo, H5P).
 * Usa hook para validar src do iframe antes de permitir.
 *
 * @param {string} dirty - HTML a ser sanitizado
 * @returns {string} HTML limpo e seguro
 */
export function sanitizeHtmlWithIframes(dirty) {
  if (!dirty || typeof dirty !== 'string') return '';

  const config = {
    ...baseConfig,
    ALLOWED_TAGS: ALLOWED_TAGS_WITH_IFRAME,
    ADD_ATTR: ['target', 'allow', 'allowfullscreen', 'frameborder', 'name'],
    ADD_TAGS: ['iframe'],
  };

  DOMPurify.addHook('uponSanitizeAttribute', (node, data) => {
    if (data.attrName === 'src' && node.tagName === 'IFRAME') {
      if (!isAllowedIframeSrc(data.attrValue)) {
        data.attrValue = '';
      }
    }
  });

  try {
    return DOMPurify.sanitize(normalizeQuillListHtml(dirty), config);
  } finally {
    DOMPurify.removeHook('uponSanitizeAttribute');
  }
}

/**
 * Remove casca de documento (DOCTYPE, html, head, body) e devolve só o fragmento embutível.
 * Evita que </body></html> no meio do index.html exportado feche o <body> principal do Moodle.
 *
 * @param {string} dirty
 * @returns {string}
 */
export function extractEmbeddableHtmlFragment(dirty) {
  if (!dirty || typeof dirty !== 'string') return '';

  let s = dirty.trim().replace(/<!DOCTYPE[^>]*>/gi, '').trim();
  if (!s) return '';

  const bodyMatch = s.match(/<body[^>]*>([\s\S]*)<\/body>/i);
  if (bodyMatch) {
    return bodyMatch[1].trim();
  }

  return s
    .replace(/<html[^>]*>/gi, '')
    .replace(/<\/html>/gi, '')
    .replace(/<head[^>]*>[\s\S]*?<\/head>/gi, '')
    .replace(/<\/?body[^>]*>/gi, '')
    .trim();
}

/**
 * Sanitiza fragmento de Template HTML para preview/export Moodle (sem html/head/body).
 * Aceita colagem de documento completo ou fragmento; sempre retorna só o miolo embutível.
 *
 * @param {string} dirty
 * @returns {string}
 */
export function prepareHtmlTemplateForEmbed(dirty) {
  const fragment = extractEmbeddableHtmlFragment(dirty);
  if (!fragment) return '';

  const allowedTags = ALLOWED_TAGS_FULL_DOC.filter((tag) => !DOCUMENT_SHELL_TAGS.has(tag));
  const config = {
    ALLOWED_TAGS: allowedTags,
    ADD_TAGS: ['style', 'iframe'],
    ALLOWED_ATTR: ALLOWED_ATTR_FULL_DOC,
    ALLOW_DATA_ATTR: false,
  };

  DOMPurify.addHook('uponSanitizeAttribute', (node, data) => {
    if (data.attrName === 'src' && node.tagName === 'IFRAME') {
      if (!isAllowedIframeSrc(data.attrValue)) {
        data.attrValue = '';
      }
    }
  });

  try {
    return DOMPurify.sanitize(fragment, config);
  } finally {
    DOMPurify.removeHook('uponSanitizeAttribute');
  }
}

/**
 * Sanitiza documento HTML completo (html, head, body, bgcolor, etc.).
 * Permite colar páginas inteiras. Remove scripts e XSS.
 * Usa WHOLE_DOCUMENT para preservar html/head/body (DOMPurify os remove por padrão).
 *
 * @param {string} dirty - HTML completo a ser sanitizado
 * @returns {string} HTML limpo e seguro
 */
export function sanitizeHtmlFullDocument(dirty) {
  if (!dirty || typeof dirty !== 'string') return '';

  const config = {
    WHOLE_DOCUMENT: true,
    ALLOWED_TAGS: ALLOWED_TAGS_FULL_DOC,
    ADD_TAGS: ['meta', 'link', 'title'],
    ADD_ATTR: [
      'target', 'allow', 'allowfullscreen', 'frameborder',
      'bgcolor', 'text', 'link', 'vlink', 'alink',
      'charset', 'content', 'http-equiv', 'name',
      'media', 'type', 'rel', 'href',
    ],
  };

  DOMPurify.addHook('uponSanitizeAttribute', (node, data) => {
    if (data.attrName === 'src' && node.tagName === 'IFRAME') {
      if (!isAllowedIframeSrc(data.attrValue)) {
        data.attrValue = '';
      }
    }
  });

  try {
    return DOMPurify.sanitize(dirty, config);
  } finally {
    DOMPurify.removeHook('uponSanitizeAttribute');
  }
}
